import Hidden from '@material-ui/core/Hidden';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { getPersonBySlug } from '@@src/apis/CatalogueApi';
import ViewMoreText from '@@src/components/Content/ViewMoreText';
import { NotFoundError } from '@@src/components/Error/Error';
import Image, { getImageUrl } from '@@src/components/Html/Image';
import GridShelfV2 from '@@src/components/Shelf/GridShelfV2';
import AlternateLinks from '@@src/components/Utils/AlternateLinks';
import OdContainer from '@@src/components/Utils/OdContainer';
import { isRtl } from '@@src/i18n';
import PageLayout from '@@src/pages/PageLayout';
import fontFamily from '@@styles/typography/fontFamily';
import OnDemand2 from '@@types/OnDemand2';
import { SSRFunctionComponent } from '@@types/ssr';

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    heading: {
      marginTop: 159,
      marginBottom: '2vw',
      fontFamily: fontFamily.secondary,

      [theme.breakpoints.down('xs')]: {
        marginTop: 117,
      },
    },
    person: {
      display: 'flex',
      flexDirection: 'row',
      gap: 60,

      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 24,
      },
    },
    personInfo: {
      display: 'flex',
      flexDirection: 'column',
      fontSize: '1.125rem',
      maxWidth: 1024,

      [theme.breakpoints.down('lg')]: {
        maxWidth: 800,
      },

      [theme.breakpoints.down('xs')]: {
        padding: '0 16px',
        fontSize: '0.875rem',
      },

      '& h1': {
        marginTop: 0,
        marginBottom: 0,
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    readMoreButton: {
      fontFamily: 'inherit',
      fontSize: 'inherit',
      color: 'inherit',
    },
    headshot: {
      width: 210,
      minWidth: 210,

      [theme.breakpoints.down('xs')]: {
        width: 138,
        minWidth: 138,
      },
    },
    headshotImg: {
      borderRadius: 105,

      [theme.breakpoints.down('xs')]: {
        borderRadius: 69,
      },
    },
    featuredItems: {
      position: 'relative',
      marginTop: 100,

      [theme.breakpoints.down('xs')]: {
        marginTop: 40,
      },
    },
  });
});

export interface PersonPageProps {
  slug: string;
  path: string;
  pageData: OnDemand2.Person | null;
}

const PersonPage: SSRFunctionComponent<PersonPageProps, { slug: string }> = ({
  slug, pageData, path, staticContext, onLoadSuccess = () => {}, onLoadError = () => {}, ...props
}) => {
  const classes = useStyles(props);
  const { t, i18n: { language } } = useTranslation(['page']);

  useEffect(() => {
    if (pageData === null) {
      onLoadError('404', 'Page Not Found');
    }

    if (pageData) {
      onLoadSuccess(pageData.name);
    }
  }, [onLoadError, onLoadSuccess, pageData]);

  if (pageData === null) {
    // if static context available, set the status code to 404 for ssr
    if (staticContext) {
      staticContext.statusCode = 404;
    }
    return <NotFoundError/>;
  }

  if (!pageData) {
    return null;
  }

  const heroImage = {
    '2:3': getImageUrl(pageData?.backgroundImage2x3),
    '16:9': getImageUrl(pageData?.backgroundImage16x9),
  };

  const pageTitle = t('page:personPage.pageTitle', { name: pageData.name, interpolation: { escapeValue: false } });
  const pageDescription = t('page:personPage.pageDescription', { name: pageData.name, interpolation: { escapeValue: false } });

  return (
    <div>
      <AlternateLinks/>
      <PageLayout
        path={path}
        heroImage={heroImage}
        seoTitle={pageTitle}
        metaDescription={pageDescription}
        ogImage={getImageUrl(pageData?.imageId)}
      >
        <OdContainer className={classes.heading} dir={isRtl(language) ? 'rtl' : 'ltr'}>
          <div className={classes.person}>
            <Hidden smUp>
              <div className={classes.personInfo}>
                <h1>{t('page:personPage.heading', { name: pageData.name, interpolation: { escapeValue: false } })}</h1>
              </div>
            </Hidden>
            <div className={classes.headshot}>
              <Image
                ratio="3:4"
                imageId={pageData.imageId}
                classes={{ image: classes.headshotImg }}
                srcSetWidths={[276, 420]}
                imageSizes={{
                  xs: 128,
                  all: 210,
                }}
                alt={t('page:personPage.photoOf', { name: pageData.name, interpolation: { escapeValue: false } })}
              />
            </div>
            <div className={classes.personInfo}>
              <Hidden xsDown>
                <h1>{t('page:personPage.heading', { name: pageData.name, interpolation: { escapeValue: false } })}</h1>
              </Hidden>
              {pageData?.biography && <ViewMoreText inputText={pageData.biography} lineClamp={6}/>}
            </div>
          </div>
        </OdContainer>

        <div className={classes.featuredItems}>
          <GridShelfV2
            collection={{
              ...pageData.featuredItems,
              title: t('page:personPage.contentFeaturing', { name: pageData.name, interpolation: { escapeValue: false } }),
            }}
            showViewAll={false}
          />
        </div>
      </PageLayout>
    </div>
  );
};

PersonPage.getInitialProps = async ({
  pathname,
  match: { params: { slug } },
  language,
}) => {
  const pageData = await getPersonBySlug(slug, language);

  return {
    slug,
    path: pathname,
    pageData,
  };
};

export default PersonPage;
